import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { auth, db } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { useOrganization } from '../../context/OrganizationContext';
import useFacebookSDK from '../../hooks/useFacebookSDK';
import { extrasDeLanzamiento, copiaMutable, desenlaceDelEvento, textoDeDescubrimiento, EVENTOS_TERMINALES, RUTA_INTERRUPTOR_COEXISTENCE, interruptorEncendido } from '../../utils/coexistenciaC0';

const API_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-sistema-bks.cloudfunctions.net';

const EMBEDDED_SIGNUP_CONFIG_ID = '954353037566169';
const SIGNUP_TIMEOUT_MS = 15000;

export default function WhatsAppEmbeddedSignup({ onSuccess }) {
  const { t } = useTranslation('channels');
  const { context } = useOrganization();
  const { FB, loading: sdkLoading, error: sdkError } = useFacebookSDK();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [code, setCode] = useState(null);
  const [signupData, setSignupData] = useState(null);

  const submittedRef = useRef(false);

  // El flujo ya terminó con un desenlace CONOCIDO y no exitoso (`FINISH_ONLY_WABA`,
  // `ERROR` o, desde C0b, el alta de coexistence). Tiene que ser un ref y no estado: el `code` de `FB.login` llega DESPUÉS del
  // postMessage, y sin esta marca rearrancaría el temporizador de :~100 para pisar a los
  // 15 s el mensaje verdadero con `popup_timeout` — justo la mentira que este commit
  // corrige. Un `useState` no lo evitaría: el callback de `FB.login` cierra sobre el
  // render del lanzamiento y leería el valor viejo.
  const terminalRef = useRef(false);

  // C0 de coexistence (COEXISTENCE-DISENO.md §6): la doble llave —org en la lista fija Y `?coexistencia=<variante>`—
  // decide el `extras` del lanzamiento; con cualquier otra cosa es exactamente el default de `extrasPorDefecto`
  // (desde C0b: v5 con `whatsapp_business_app_onboarding` apagada; antes `{ setup: {} }`). En descubrimiento
  // NADA va al callback: ni `code`, ni `/register`, ni Firestore; sólo se enseña lo que devolvió el flujo.
  //
  // EL LANZAMIENTO SE FIJA EN EL CLIC, no en el render (🔴 de la revisión de c87a6ab): el listener de `postMessage`
  // y el callback de `FB.login` leen el lanzamiento, y si el ref se reasignara en cada render, cualquier cambio del
  // contexto de organización o del `search` con el popup abierto (hoy el OrgSwitcher sólo vive en el Dashboard, pero
  // un back/forward o un `switchContext` desde cualquier sitio bastan) lo pondría a `false` a mitad de flujo y el
  // `code` de un lanzamiento en descubrimiento acabaría en el callback —con la org nueva—. Por eso `descubrimientoRef`
  // sólo se escribe dentro de `launchEmbeddedSignup`, junto con el `extras` que se manda (el callback de `FB.login`
  // usa directamente la constante de su clic), y `lanzado` guarda ESE lanzamiento para el panel. `lanzamientoPrevisto`
  // es lo que pasaría si se abre el popup AHORA: el aviso previo, y el aviso rojo si ya no casa con `lanzado`.
  // C9: el interruptor global de coexistence, en vivo. Por defecto encendido (sin documento, o si la lectura falla).
  const [coexistenceHabilitada, setCoexistenceHabilitada] = useState(true);
  // 🟠1 de la revisión de C9–C12: el lanzamiento con la app Business ENCENDIDA sólo se midió con un número de la app; un
  // número normal sigue con el lanzamiento medido (C0b) salvo que el cliente declare que su número está en la app.
  const [usaLaApp, setUsaLaApp] = useState(false);
  useEffect(() => {
    const [coleccion, documento] = RUTA_INTERRUPTOR_COEXISTENCE.split('/');
    try {
      return onSnapshot(doc(db, coleccion, documento), (snap) => setCoexistenceHabilitada(interruptorEncendido(snap.exists() ? snap.data() : null)), (err) => {
        // Fail-open a propósito (el callback decide igual); se registra porque el listener ya no se reabre (🟠3).
        console.warn('[EmbeddedSignup] interruptor de coexistence ilegible; se asume encendido', { code: err?.code || null });
        setCoexistenceHabilitada(true);
      });
    } catch (_) { setCoexistenceHabilitada(true); return undefined; }
  }, []);
  const leerLanzamiento = () => extrasDeLanzamiento({
    orgId: context?.mode === 'organization' ? context.orgId : null,
    search: typeof window !== 'undefined' ? window.location.search : '',
    coexistenceHabilitada: coexistenceHabilitada && usaLaApp,
  });
  const lanzamientoPrevisto = leerLanzamiento();
  const descubrimientoRef = useRef(false);
  // C5: fijado en el clic, como el de descubrimiento. Con `true`, el alta de coexistence SÍ manda el `code`.
  const coexistenceRef = useRef(false);
  // 🟡6 de C0b (C12): v5 no manda `version` en el postMessage; la del lanzamiento (fijada en el clic) es la que se guarda.
  const versionRef = useRef(null);
  const [lanzado, setLanzado] = useState(null);
  const [descubrimiento, setDescubrimiento] = useState({ eventos: [], codeLlego: null });

  const configReady = EMBEDDED_SIGNUP_CONFIG_ID !== 'PENDING_META_CONFIG';

  const resetFlow = () => {
    submittedRef.current = false;
    terminalRef.current = false;
    setCode(null);
    setSignupData(null);
    setError(null);
    setResult(null);
    setSubmitting(false);
    setDescubrimiento({ eventos: [], codeLlego: null });
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.origin !== 'https://www.facebook.com' &&
        event.origin !== 'https://web.facebook.com'
      ) return;
      let data;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch (_) {
        return;
      }
      if (!data || data.type !== 'WA_EMBEDDED_SIGNUP') return;

      console.log('[EmbeddedSignup] postMessage event:', JSON.stringify(data));

      // Descubrimiento (C0): se guarda el evento tal cual para enseñarlo y, si es un desenlace, se cierra el flujo.
      // Nunca se llega a `setSignupData`: sin él no hay envío al callback (ver el efecto de `submitToBackend`).
      if (descubrimientoRef.current) {
        setDescubrimiento((d) => ({ ...d, eventos: [...d.eventos, data] }));
        if (EVENTOS_TERMINALES.includes(data.event)) {
          terminalRef.current = true;
          setSubmitting(false);
        }
        return;
      }

      // La decisión vive en `desenlaceDelEvento` (función pura con el payload real de v5 como fixture; d74). Aquí sólo
      // se aplican sus efectos. `evento` es el desenlace que DECLARA Meta (desde v4 se lee, no se infiere del
      // contenido); `version` no está documentado en ninguna versión y se captura de los dos sitios posibles;
      // `wabaId` es `waba_id` (v4) o `messaging_account_ids[0]` (v5, que NO trae `waba_id`).
      const desenlace = desenlaceDelEvento(data);
      const { evento, phoneId, wabaId, version } = desenlace;

      if (desenlace.tipo === 'coexistence') {
        // C5 (27-oct-2026): con el lanzamiento de coexistence (org habilitada, fijado en el clic) el alta de la app
        // WhatsApp Business SÍ se manda al callback, con `is_on_biz_app: true` para que su puerta la reconozca aunque
        // el evento cambie. Sin él, lo de C0b: Meta ya enlazó la WABA a PUX (PARTNER_ADDED) pero el callback haría
        // `/register` sobre un número registrado en la app, así que ese `code` NO se envía.
        // Va ANTES de cualquier otra rama: es la única que, de caer en «fijar» sin la llave, haría daño.
        if (coexistenceRef.current && phoneId && wabaId) {
          console.log('[EmbeddedSignup] coexistence onboarding → callback', { evento, cuentas: desenlace.cuentas });
          setSignupData({ phone_number_id: phoneId, waba_id: wabaId, signupEvent: evento, signupVersion: version ?? versionRef.current, is_on_biz_app: true });
          return;
        }
        console.warn('[EmbeddedSignup] coexistence onboarding not supported yet', { evento, cuentas: desenlace.cuentas, sinNumero: !phoneId || !wabaId });
        terminalRef.current = true;
        // Con la llave puesta, lo que falló es que el FINISH no trajo número o WABA: se dice eso, no «PUX no puede» (🟡9).
        setError(coexistenceRef.current ? 'coexistence_sin_numero' : 'coexistence_not_supported');
        setSubmitting(false);
        setCode(null);
        return;
      }

      if (desenlace.tipo === 'sin_numero') {
        // El cliente compartió la cuenta de WhatsApp Business pero terminó SIN número.
        // No es un fallo nuestro ni una espera agotada: es un final legítimo de v4. Antes
        // caía en el temporizador y se le decía «tardó demasiado», que era falso.
        console.warn('[EmbeddedSignup] finished without phone number', { evento });
        terminalRef.current = true;
        setError('finished_without_number');
        setSubmitting(false);
        setCode(null);
        return;
      }

      if (desenlace.tipo === 'error') {
        console.error('[EmbeddedSignup] Meta reported ERROR', { evento });
        terminalRef.current = true;
        setError('meta_error');
        setSubmitting(false);
        setCode(null);
        return;
      }

      if (desenlace.tipo === 'fijar') {
        if (desenlace.cuentas > 1) {
          // v5 puede traer varias WABAs; hoy no sabemos cuál es la del número. Se sigue con la primera y queda
          // escrito: el callback valida el par WABA/número contra Meta (5b) y rechaza un par cruzado.
          console.warn('[EmbeddedSignup] several messaging_account_ids, using the first', { cuentas: desenlace.cuentas });
        }
        setSignupData({
          phone_number_id: phoneId,
          waba_id: wabaId,
          signupEvent: evento,
          signupVersion: version ?? versionRef.current,
          // Viaja para que la guarda del callback sea alcanzable desde NUESTRO cliente (🟡3 de la revisión de
          // d39d32d): aquí siempre es false (la rama coexistence ya salió), y si un cambio futuro del listener dejara
          // pasar un alta de la app, el callback la para por este campo y no sólo por el evento.
          is_on_biz_app: desenlace.enApp,
        });
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!code || !signupData || submittedRef.current) return;
    // Defensa en profundidad (C0): en descubrimiento ni `code` ni `signupData` llegan a fijarse, pero si algún
    // camino nuevo los fijara, el envío al callback sigue cerrado.
    if (descubrimientoRef.current) return;
    submittedRef.current = true;
    submitToBackend({ code, ...signupData });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, signupData]);

  useEffect(() => {
    // `terminalRef` va en el guard, no sólo el `setCode(null)` del listener: el `code`
    // puede llegar DESPUÉS del postMessage y volver a montar este temporizador.
    if (!code || signupData || submittedRef.current || terminalRef.current) return;
    const timer = setTimeout(() => {
      if (!submittedRef.current) {
        console.warn('[EmbeddedSignup] timeout waiting for postMessage');
        setError('popup_timeout');
        setSubmitting(false);
        setCode(null);
      }
    }, SIGNUP_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [code, signupData]);

  const submitToBackend = async ({ code, phone_number_id, waba_id, signupEvent, signupVersion, is_on_biz_app }) => {
    try {
      console.log('[EmbeddedSignup] submitting to backend', { phone_number_id, waba_id, signupEvent, signupVersion, is_on_biz_app });
      const idToken = await auth.currentUser.getIdToken();
      const organizationId = context?.mode === 'organization' ? context.orgId : null;
      const response = await fetch(`${API_URL}/integrations_whatsappbusiness_callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        // `signupEvent` e `is_on_biz_app` DECIDEN en el callback (desde C0b/C5: un alta de la app
        // Business va por coexistence o se rechaza); `signupVersion` queda guardado (desde C12 es
        // la del postMessage o, si v5 no la manda, la del lanzamiento).
        body: JSON.stringify({ code, phone_number_id, waba_id, organizationId, signupEvent, signupVersion, is_on_biz_app }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.ok) {
        console.log('[EmbeddedSignup] backend success', { numeroId: data.numeroId, isUpdate: data.isUpdate });
        setResult(data);
        setSubmitting(false);
        if (onSuccess) onSuccess(data.numeroId);
      } else {
        console.error('[EmbeddedSignup] backend error', data);
        setError(data.error || 'internal_error');
        setSubmitting(false);
      }
    } catch (err) {
      console.error('[EmbeddedSignup] network error', err);
      setError('internal_error');
      setSubmitting(false);
    } finally {
      setCode(null);
      setSignupData(null);
    }
  };

  const launchEmbeddedSignup = () => {
    if (!FB || !configReady) return;
    resetFlow();
    setSubmitting(true);
    // Aquí, y sólo aquí, se decide y se fija el lanzamiento (ver el comentario de `descubrimientoRef`).
    const lanzamiento = leerLanzamiento();
    descubrimientoRef.current = lanzamiento.descubrimiento;
    coexistenceRef.current = lanzamiento.coexistence === true;
    versionRef.current = lanzamiento.extras?.version ?? null;
    setLanzado(lanzamiento);

    FB.login(
      (response) => {
        console.log('[EmbeddedSignup] FB.login response status:', response?.status);
        // Descubrimiento (C0): sólo se anota SI llegó el `code` (booleano); el `code` no se guarda ni se envía.
        // Lee el lanzamiento de ESTE clic (constante del cierre), no el ref: el evento terminal reactiva el botón
        // antes de que llegue este callback, y un relanzamiento en ese hueco no debe cambiar cómo se lee (🟡2 de la
        // revisión acotada de bfeca03).
        if (lanzamiento.descubrimiento) {
          setDescubrimiento((d) => ({ ...d, codeLlego: Boolean(response?.authResponse?.code) }));
          setSubmitting(false);
          return;
        }
        if (response?.authResponse?.code) {
          setCode(response.authResponse.code);
        } else {
          // `terminalRef` primero: si Meta ya dijo ERROR o FINISH_ONLY_WABA por
          // postMessage, no hay `code` y este camino pintaría «se cerró la ventana»
          // encima del motivo real. (`signupData` aquí es del cierre del lanzamiento y
          // siempre vale null; el ref es el que de verdad discrimina.)
          if (!terminalRef.current && !signupData && !submittedRef.current) {
            setError('popup_closed');
            setSubmitting(false);
          }
        }
      },
      {
        config_id: EMBEDDED_SIGNUP_CONFIG_ID,
        response_type: 'code',
        override_default_response_type: true,
        // ═══════════════════════════════════════════════════════════════════════════
        //  v4 (7-sep-2026). Meta deprecia v2 el 15-oct-2026.
        //
        //  LA VERSIÓN YA NO SE ELIGE DESDE EL CÓDIGO. La fija la configuración de
        //  Facebook Login for Business: «Selecting the products will automatically set
        //  you to v4». Medido en el panel el 7-sep-2026 sobre `954353037566169`: variación
        //  «Registro insertado de WhatsApp» (la que v4 exige) y producto WhatsApp Cloud
        //  API ya marcado. La config ya cumple los tres pasos de v4; lo único desalineado
        //  era este objeto.
        //
        //  Qué se quitó, y por qué cada cosa:
        //
        //  · `version: 2` — el sample oficial de extras DE v2 no lleva `version`. Ese
        //    parámetro sólo aparece en v3 (`'v3'`) y v4-public-preview
        //    (`'v4-public-preview'`), y siempre como STRING con prefijo `v`, nunca como
        //    número. Declaraba una versión con un parámetro que esa versión no define.
        //
        //  · `sessionInfoVersion: 2` — es de v2 y sólo de v2 («Partners are required to
        //    add a sessionInfoVersion»); en v4 desaparece. OJO, porque invita al error:
        //    NO es la versión de Embedded Signup, es la del payload de sessionInfo. Son
        //    dos ejes distintos, y el valor vigente de ese eje es "3", no 2.
        //
        //  · `feature: 'whatsapp_embedded_signup'` — no figura en NINGÚN sample oficial de
        //    NINGUNA versión (v2, v3, v4); el de v2 usa `features`, en plural y array. Se
        //    quita en vez de dejarlo «por si acaso» porque la única afirmación que la
        //    documentación hace sobre los extras de v4 es que el objeto va vacío: guardar
        //    dentro una clave que no sabemos leer contradice justo esa frase. Su riesgo
        //    es ilimitado y no medible; el de quitarla es acotado y se mide en el alta
        //    e2e que esta migración exige de todos modos.
        //
        //  `setup: {}` SE QUEDA: está en el sample ejecutable de la página Implementation
        //  de v4. (La página Versions escribe `extras: {}` a secas — discrepancia dentro
        //  de la propia documentación de Meta; se sigue el sample ejecutable.)
        //
        //  RESTRICCIÓN QUE ESTA CONFIG YA DEJÓ FIJADA: el paso Products dice «This can't
        //  be changed later», igual que la variación. Añadir CTWA, Marketing Messages o
        //  Conversions API en el futuro exigirá una configuración NUEVA y, por tanto, otro
        //  `EMBEDDED_SIGNUP_CONFIG_ID`. La decisión de quedarse sólo con Cloud API ya está
        //  tomada; no es reversible sobre esta config.
        //
        //  C0 de coexistence (23-oct-2026): el objeto `extras` vive ahora en
        //  `src/utils/coexistenciaC0.js` (`extrasPorDefecto`), y sólo con la doble llave
        //  (org en la lista fija + `?coexistencia=<variante>`) sale otra cosa.
        //  C0b (26-oct-2026): el default pasa de `{ setup: {} }` (v4) a `version: 'v5'`
        //  con `whatsapp_business_app_onboarding` APAGADA, para todas las orgs (medido:
        //  esa forma muestra el número de la app como «No cumple los requisitos»).
        // ═══════════════════════════════════════════════════════════════════════════
        //  El SDK recibe una COPIA mutable: lo que guardamos y enseñamos va congelado
        //  (H4) y nadie ha medido si FB.login escribe en `extras` (🟡2 de la revisión
        //  de d39d32d).
        extras: copiaMutable(lanzamiento.extras),
      }
    );
  };

  if (!configReady) {
    return (
      <div style={infoBoxStyle}>
        {t('whatsappBusiness.comingSoon')}
      </div>
    );
  }

  if (result) {
    return (
      <div style={successBoxStyle}>
        <h3 style={{ margin: 0, marginBottom: 8 }}>{t('whatsappBusiness.success')}</h3>
        <p style={{ margin: 0, marginBottom: 16, color: '#52525b' }}>
          {t('whatsappBusiness.successDescription')}
        </p>
        <p style={{ margin: 0, marginBottom: 16 }}>
          <strong>{result.display_phone_number}</strong>
          {result.verified_name ? ` — ${result.verified_name}` : ''}
        </p>
        {result.coexistence && (
          // C5: el número sigue en la app WhatsApp Business. Lo que el titular tiene que saber, en el momento en que
          // importa (los textos, en channels.json → whatsappBusiness.coexistence; decisión de Rodrigo, 27-oct).
          <div data-testid="coexistence-exito" style={{ ...pinBoxStyle, textAlign: 'left', fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('whatsappBusiness.coexistence.successTitle')}</div>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>{t('whatsappBusiness.coexistence.puntoBot')}</li>
              <li>{t('whatsappBusiness.coexistence.punto14Dias')}</li>
              <li>{t('whatsappBusiness.coexistence.puntoHistorial')}</li>
            </ul>
          </div>
        )}
        {result.pin && (
          <div style={pinBoxStyle}>
            <div style={{ fontSize: 12, color: '#52525b', marginBottom: 4 }}>
              {t('whatsappBusiness.pinLabel')}
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: 6, fontFamily: 'monospace' }}>
              {result.pin}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.5, color: '#52525b', marginTop: 12, textAlign: 'left' }}>
              {t('whatsappBusiness.pinHelp')}
            </div>
            {result.emailSent && (
              <div style={{ fontSize: 12, color: '#71717a', marginTop: 6, textAlign: 'left' }}>
                {t('whatsappBusiness.pinAlsoEmailed')}
              </div>
            )}
          </div>
        )}
        <button onClick={resetFlow} style={secondaryButtonStyle}>
          {t('whatsappBusiness.connectButton')}
        </button>
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      {lanzamientoPrevisto.descubrimiento && !submitting && (
        // Aviso previo de C0 (🟠3 de la revisión: el parámetro se puede perder al limpiar la URL): si este aviso no se
        // ve ANTES de abrir el popup, el lanzamiento será el normal y NO hay que completar el flujo. Se pinta siempre
        // que el PRÓXIMO clic vaya a ser descubrimiento (también después de un flujo anterior: 🟠1 de la revisión
        // acotada de bfeca03). Texto fijo a propósito: sólo lo ve la org de la lista con `?coexistencia=`.
        <pre data-testid="descubrimiento-c0-previsto" style={descubrimientoStyle}>
          {`MODO DESCUBRIMIENTO (C0) ACTIVO · variante ${lanzamientoPrevisto.variante} · se aplicará al abrir el popup; nada irá al servidor.\nextras: ${JSON.stringify(lanzamientoPrevisto.extras)}`}
        </pre>
      )}
      {lanzado && lanzado.descubrimiento && !submitting && !lanzamientoPrevisto.descubrimiento && (
        // Las llaves cambiaron después de un descubrimiento (otra org, otra URL): el panel de abajo es la evidencia
        // de ESE flujo, pero el próximo clic sería un alta NORMAL. Se dice en rojo (🟠1 de la revisión acotada).
        <div data-testid="descubrimiento-c0-caducado" style={errorBoxStyle}>
          {'ATENCIÓN: las llaves del modo descubrimiento ya no se cumplen. El PRÓXIMO clic en «conectar» será un alta NORMAL (irá al servidor). Vuelve a abrir la página con ?coexistencia=<variante>.'}
        </div>
      )}
      {lanzado && lanzado.descubrimiento && (
        // Panel de C0 con el lanzamiento FIJADO en el clic (no el del render actual): es la evidencia que se copia tal
        // cual (COEXISTENCE-DISENO.md §6).
        <pre data-testid="descubrimiento-c0" style={descubrimientoStyle}>
          {textoDeDescubrimiento({ variante: lanzado.variante, extras: lanzado.extras, codeLlego: descubrimiento.codeLlego, eventos: descubrimiento.eventos })}
        </pre>
      )}
      {error && (
        <div style={errorBoxStyle}>
          {t(`whatsappBusiness.errors.${error}`, { defaultValue: t('whatsappBusiness.errors.internal_error') })}
        </div>
      )}

      <p style={{ marginTop: 0, marginBottom: 12, color: '#52525b' }}>
        {t('whatsappBusiness.connectDescription')}
      </p>
      {/* `coexistenceNote` retirado el 14-ago-2026. Afirmaba que el cliente podía
          mantener activa su WhatsApp Business App mientras PUX gestiona los
          mensajes — o sea Coexistence. Este componente lanza `FB.login` con el
          default de `extrasPorDefecto` (desde C0b: v5 con el onboarding de la app
          Business APAGADO a propósito; antes `setup: {}` sin `featureType`): el
          Embedded Signup estándar, sin la ruta para usuarios de la Business App.
          Reponerlo es la línea C1–C5 de COEXISTENCE-DISENO.md. */}
      <p style={{ marginTop: 0, marginBottom: 24, color: '#52525b', fontSize: 14 }}>
        {t('whatsappBusiness.noTechNote')}
      </p>
      {coexistenceHabilitada && context?.mode === 'organization' && (
        // C5/C9: con el interruptor encendido, cualquier organización puede conectar el número de su app WhatsApp Business;
        // la casilla es lo que cambia el lanzamiento (🟠1): sin marcarla, el alta normal de siempre.
        <div data-testid="coexistence-intro" style={{ ...infoBoxStyle, marginBottom: 20, fontSize: 13, lineHeight: 1.6 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('whatsappBusiness.coexistence.introTitle')}</div>
          <label data-testid="coexistence-casilla" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={usaLaApp} disabled={submitting} onChange={(e) => setUsaLaApp(e.target.checked)} style={{ marginTop: 3 }} />
            <span>{t('whatsappBusiness.coexistence.casilla')}</span>
          </label>
          <div style={{ marginBottom: 8 }}>{t('whatsappBusiness.coexistence.intro')}</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>{t('whatsappBusiness.coexistence.puntoBot')}</li>
            <li>{t('whatsappBusiness.coexistence.punto14Dias')}</li>
            <li>{t('whatsappBusiness.coexistence.puntoHistorial')}</li>
            <li>{t('whatsappBusiness.coexistence.puntoPierdes')}</li>
          </ul>
        </div>
      )}

      <button
        onClick={launchEmbeddedSignup}
        disabled={sdkLoading || submitting || !FB}
        style={{
          ...primaryButtonStyle,
          background: sdkLoading || submitting || !FB ? '#ccc' : '#25D366',
          cursor: sdkLoading || submitting || !FB ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting
          ? t('whatsappBusiness.connecting')
          : sdkLoading
            ? '…'
            : t('whatsappBusiness.connectButton')}
      </button>

      {sdkError && (
        <div style={{ marginTop: 12, fontSize: 13, color: '#c33' }}>
          {t('whatsappBusiness.errors.sdk_load_failed')}
        </div>
      )}
    </div>
  );
}

const cardStyle = {
  background: 'white',
  border: '1px solid #e4e4e7',
  borderRadius: 12,
  padding: 24,
  maxWidth: 560,
};
const primaryButtonStyle = {
  border: 'none',
  color: 'white',
  borderRadius: 8,
  fontSize: 15,
  fontWeight: 500,
  padding: '12px 20px',
};
const secondaryButtonStyle = {
  border: '1px solid #ddd',
  background: 'white',
  borderRadius: 8,
  fontSize: 14,
  padding: '10px 16px',
  cursor: 'pointer',
};
const errorBoxStyle = {
  padding: 12,
  marginBottom: 16,
  background: '#fee',
  border: '1px solid #fcc',
  borderRadius: 6,
  color: '#c33',
};

const descubrimientoStyle = {
  padding: 12,
  marginBottom: 16,
  background: '#fff7ed',
  border: '1px solid #fdba74',
  borderRadius: 6,
  color: '#7c2d12',
  fontSize: 12,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  maxHeight: 320,
  overflow: 'auto',
};
const infoBoxStyle = {
  padding: 16,
  background: '#f4f4f5',
  border: '1px solid #e4e4e7',
  borderRadius: 8,
  color: '#52525b',
  maxWidth: 560,
};
const successBoxStyle = {
  background: '#f0fdf4',
  border: '1px solid #bbf7d0',
  borderRadius: 12,
  padding: 24,
  maxWidth: 560,
};
const pinBoxStyle = {
  background: '#f4f4f5',
  border: '2px solid #e4e4e7',
  borderRadius: 8,
  padding: 16,
  textAlign: 'center',
  marginBottom: 16,
};
